/**
 * Bash's phone (spec docs/superpowers/specs/2026-10-14-bash-phone-design.md, frame
 * docs/superpowers/plans/2026-10-14-bash-phone/00-program.md): every cap and number the frame fixes,
 * in one place. The server and the web import them; the relay receives the media tuning in each
 * POST /sessions body (PHONE_MEDIA_TUNING), so it holds no second copy.
 */
import { LIVE_VOICE_USD_PER_MINUTE } from '../composer/live.js';
import type { PhoneLineKind, PhoneMediaTuning } from './types.js';

export const PHONE_COUNTRIES = ['US', 'IN'] as const;
export type PhoneCountry = (typeof PHONE_COUNTRIES)[number];
/** Whose own numbers Bash verifies and calls: an owner may be in the US or India (the founder's ruling, 2026-10-15). */
export const PHONE_OWNER_COUNTRIES: readonly PhoneCountry[] = ['US', 'IN'];
/** Where Bash's own numbers come from: the pool rents only these. Until P1c adds 'IN' (spec §12), an Indian owner gets a US number. */
export const PHONE_NUMBER_COUNTRIES: readonly PhoneCountry[] = ['US'];
export const PHONE_DIAL_CODES: Readonly<Record<PhoneCountry, string>> = { US: '1', IN: '91' };

// ── Verification (spec §5.4) ────────────────────────────────────────────────
export const PHONE_VERIFY_DAILY_CAP = 3;
export const PHONE_VERIFY_LIFETIME_CAP = 10;
/** A number whose verification failed this many times is blocked for that account. */
export const PHONE_VERIFY_FAILS_BLOCK = 2;
export const PHONE_VERIFY_CODE_DIGITS = 4;

// ── Keypad gathers (spec §4.1, §5.2; Review Focus 4) ────────────────────────
/** Wrong entries one call allows before it ends. A partial entry is wrong; a timeout is not. */
export const PHONE_GATHER_MAX_WRONG = 3;
/** Silent keypads one call re-prompts before it ends. A timeout never counts as a failure. */
export const PHONE_GATHER_MAX_TIMEOUTS = 2;
export const PHONE_GATHER_TIMEOUT_MS = 15_000;

// ── PIN (spec §5.2) ─────────────────────────────────────────────────────────
export const PHONE_PIN_MIN_DIGITS = 4;
export const PHONE_PIN_MAX_DIGITS = 6;
export const PHONE_PIN_LOCK_FAILS = 5;
export const PHONE_PIN_LOCK_WINDOW_MS = 24 * 60 * 60_000;

// ── The stream ticket (spec §3.2) ───────────────────────────────────────────
export const PHONE_TICKET_TTL_MS = 60_000;

// ── The phone line cap (spec §3.4) ──────────────────────────────────────────
/** Phone lines this process holds at most: they share OpenAI's session cap with the app Line. */
export const PHONE_MAX_LINES = 20;
/** A kind is admitted while open phone lines < PHONE_MAX_LINES - its reserve: outsiders shed first, owners last. */
export const PHONE_LINE_RESERVE: Readonly<Record<PhoneLineKind, number>> = { outsider: 4, ring: 2, owner: 0 };

// ── Numbers (spec §3.1) ─────────────────────────────────────────────────────
/** Founder ruling 2026-10-17: 3, from 20 (numbers rent by the month, and only paid plans hold one). */
export const PHONE_POOL_TARGET = 3;
/** Numbers one sweep buys at most, so a fault can never buy the carrier's whole stock. */
export const PHONE_POOL_BUY_PER_RUN = 5;
/** Assignments in 24 h above this page someone: the pool is draining faster than usual. */
export const PHONE_POOL_DRAIN_ALERT_PER_DAY = 20;
export const PHONE_PENDING_DAYS = 7;
/** A number whose owner left the paid plans is paused this long, then released (founder 2026-10-17). */
export const PHONE_PAUSE_DAYS = 30;
export const PHONE_POOL_SWEEP_MS = 10 * 60_000;

// ── Money (spec §8) ─────────────────────────────────────────────────────────
/** Founder ruling 2026-10-17: no free phone minutes. Only paid plans hold a number, and every minute is billed at cost. */
export const PHONE_FREE_SECONDS_PER_MONTH = 0;
export const PHONE_WALLET_TICK_MS = 60_000;

// ── Consent (spec §6.2) ─────────────────────────────────────────────────────
export const PHONE_CONSENT_VERSION = '2026-10-14';
export const PHONE_CONSENT_TEXT = 'Bash will call this number with an AI voice. Calls are transcribed.';

// ── The relay's media (P0-A Q2 and Q4). Part B may retune the VAD against real line echo. ──
export const PHONE_MEDIA_FRAME_MS = 20;
/** 20 ms of 8 kHz mu-law. */
export const PHONE_MEDIA_FRAME_BYTES = 160;
/** Energy onset: a frame's RMS above this, in linear PCM16 units. */
export const PHONE_VAD_RMS = 500;
/** ...for this many frames in a row sends the carrier `clear` (measured 57 to 59 ms after the first voiced frame). */
export const PHONE_VAD_ONSET_FRAMES = 2;
/** A confirmed onset (about 250 ms of voice: a cough or "mm-hmm" is shorter) gates Bash's output. */
export const PHONE_VAD_CONFIRM_MS = 250;
/** A quiet gap longer than this inside the voice starts the count over. */
export const PHONE_VAD_GAP_MS = 80;
/** The gate reopens once Bash's own output has been quiet this long. */
export const PHONE_OUTPUT_QUIET_MS = 300;
/** An output frame at or under this RMS is quiet. Live sends silence as real frames. */
export const PHONE_OUTPUT_QUIET_RMS = 200;
/** Inbound frames the relay's clock holds at most; the oldest drop past it, so latency never grows. */
export const PHONE_MEDIA_MAX_QUEUE_FRAMES = 5;

// ── The relay's sessions ────────────────────────────────────────────────────
/** The door's event socket may be gone this long before the relay ends the call. */
export const PHONE_RELAY_DOOR_GRACE_MS = 5_000;
/**
 * A pre-opened session with no media this long is closed. It costs nothing (billing counts media time).
 * It must outlive the longest PIN gather, during which no media flows (pinned by a test).
 */
export const PHONE_RELAY_PREOPEN_TTL_MS = 180_000;
export const PHONE_RELAY_START_TIMEOUT_MS = 10_000;
/** The door's commands the relay forwards to OpenAI. Everything else, audio above all, is refused. */
export const PHONE_RELAY_COMMANDS = [
  'session.commentary.append',
  'session.thinking.append',
  'session.instructions.append',
  'session.input_audio.mute',
  'session.input_audio.unmute',
  'session.close',
] as const;

// ── The relay's voice engine (its PHONE_VOICE_ENGINE; the server reads it off session.started) ──
export const PHONE_VOICE_ENGINES = ['openai', 'grok'] as const;
export type PhoneVoiceEngine = (typeof PHONE_VOICE_ENGINES)[number];
/** A model minute on each engine: gpt-live-1 is the Line's own price row, Grok Voice is xAI's list price (read 2026-10-15). */
export const PHONE_ENGINE_USD_PER_MINUTE: Readonly<Record<PhoneVoiceEngine, number>> = { openai: LIVE_VOICE_USD_PER_MINUTE, grok: 0.08 };
/** The vendor that bills each engine's minutes: the ledger row names it, so a Grok minute never reads as OpenAI's. */
export const PHONE_ENGINE_PROVIDER: Readonly<Record<PhoneVoiceEngine, 'openai' | 'xai'>> = { openai: 'openai', grok: 'xai' };

export const PHONE_MEDIA_TUNING: PhoneMediaTuning = {
  frameMs: PHONE_MEDIA_FRAME_MS,
  frameBytes: PHONE_MEDIA_FRAME_BYTES,
  vadRms: PHONE_VAD_RMS,
  onsetFrames: PHONE_VAD_ONSET_FRAMES,
  confirmMs: PHONE_VAD_CONFIRM_MS,
  gapMs: PHONE_VAD_GAP_MS,
  quietMs: PHONE_OUTPUT_QUIET_MS,
  quietRms: PHONE_OUTPUT_QUIET_RMS,
  maxQueueFrames: PHONE_MEDIA_MAX_QUEUE_FRAMES,
  doorGraceMs: PHONE_RELAY_DOOR_GRACE_MS,
  preopenTtlMs: PHONE_RELAY_PREOPEN_TTL_MS,
};
